import { useContext, useMemo, useState } from 'react';
import { ORDER_FULFILLMENT_STATUSES, SCREENS } from '../../services/exports/Constants';
import LoadingIndicator from '../loading/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import useRouter from '../../hooks/navigation/useRouter';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { OrderStatusContext } from '../../providers/OrderStatusProvider';
import { ChevronRight, Clock, XIcon } from 'lucide-react';
import classNames from 'classnames';
import CheckmarkIcon from '../../assets/icons/CheckmarkIcon';
import useTheme from '../../hooks/ui/useTheme';
import useScreenType from '../../hooks/utility/useScreenType';
import { formatTableName, getOrderTableName, isOnSiteMethod, orderTypeKey } from '../../utils/on-site';

interface Props {
  className?: string;
}

export default function OngoingOrder(props: Props) {
  const { className } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OngoingOrder' });
  const { t: tTable } = useTranslation(null, { keyPrefix: 'Components:OrderType:TableOrdering' });

  const { company } = useSelector((store: StoreInterface) => store.initialData);
  const { isDesktop } = useScreenType();
  const { navigate } = useRouter();
  const { formatTime } = useAppMethods();
  const { ongoingOrder, removeOngoingOrder } = useOrderFlow();
  const [deletingOrder, setDeletingOrder] = useState(false);
  const { disconnect } = useContext(OrderStatusContext);
  const theme = useTheme();
  const isRejected = useMemo(
    () => ongoingOrder.fulfillment.status === ORDER_FULFILLMENT_STATUSES.REJECTED,
    [ongoingOrder.fulfillment.status],
  );

  async function deleteOrder() {
    setDeletingOrder(true);
    removeOngoingOrder(ongoingOrder.id);
    disconnect();

    setTimeout(() => setDeletingOrder(false), 500);
  }

  function goToOrder() {
    navigate({
      web: {
        to: `${SCREENS.ORDER_DETAILS}/${ongoingOrder.uuid}`,
      },
    });
  }

  function PickUpDeliveryTime({ className }: { className?: string }) {
    return (
      <div
        className={classNames(
          'flex items-center gap-2 bg-card lg:px-3 lg:py-1.5 px-2 py-1 rounded-full border border-border',
          className,
        )}
      >
        <Clock className="lg:h-4 lg:w-4 h-3 w-3" />
        <div className={'flex flex-row relative'}>
          <h6 className="lg:text-sm text-xs">
            {isOnSiteMethod(ongoingOrder.method) ? (
              <>
                {[tTable('table_ordering'), formatTableName(getOrderTableName(ongoingOrder), tTable('table'))]
                  .filter(Boolean)
                  .join(' · ')}
                {' · '}
              </>
            ) : (
              <>{t(orderTypeKey(ongoingOrder.method))} </>
            )}
            <span>{formatTime(ongoingOrder.fulfillment.estimated_pickup_time)}</span>
          </h6>
        </div>
      </div>
    );
  }

  function renderLabel() {
    const getTitle = () => {
      if (!company?.has_scheduled_orders) {
        return t('ongoing_order');
      }

      switch (ongoingOrder.fulfillment.status) {
        case ORDER_FULFILLMENT_STATUSES.REJECTED:
          return t('status.rejected.title');
        default:
          return t('ongoing_order');
      }
    };

    const getDescription = () => {
      if (!company?.has_scheduled_orders) {
        return t('view_more');
      }

      switch (ongoingOrder.fulfillment.status) {
        case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
        case ORDER_FULFILLMENT_STATUSES.REJECTED:
          return t('status.rejected.description');
        case ORDER_FULFILLMENT_STATUSES.CREATED:
          return t('status.created.description');
        case ORDER_FULFILLMENT_STATUSES.REVIEWED:
          return t('status.reviewed.description');
        default:
          return null;
      }
    };

    const getIcon = () => {
      switch (ongoingOrder.fulfillment.status) {
        case ORDER_FULFILLMENT_STATUSES.REJECTED:
          return null;
        case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
        case ORDER_FULFILLMENT_STATUSES.PREPARING:
        case ORDER_FULFILLMENT_STATUSES.CREATED:
        case ORDER_FULFILLMENT_STATUSES.REVIEWED:
          return <LoadingIndicator sm={13} md={16} lg={18} />;
        case ORDER_FULFILLMENT_STATUSES.READY:
        case ORDER_FULFILLMENT_STATUSES.PICKED:
          return <CheckmarkIcon className="w-[14px] h-[14px] mt-[2px]" color={theme.color.primary} />;
        default:
          return null;
      }
    };

    return (
      <div className={'flex flex-col gap-1 items-start'}>
        <div className="flex items-center gap-2">
          {getIcon()}
          <h6
            className={classNames('text-sm underline', {
              'text-white': isRejected,
            })}
          >
            {getTitle()} <p className="inline font-semibold">#{ongoingOrder.fulfillment.number}</p>
          </h6>
        </div>

        <p
          className={classNames('text-xs text-muted-foreground', {
            'text-white': isRejected,
          })}
        >
          {getDescription()}
        </p>
        {!isRejected && <PickUpDeliveryTime className="!p-none border-none lg:hidden block" />}
      </div>
    );
  }

  function renderCloseIcon() {
    return (
      <button
        className={classNames(
          'flex items-center justify-center w-6 h-6 z-20 rounded-full bg-card border border-border',
          {
            'bg-danger': isRejected,
            'hover:bg-gray-1200': !isRejected,
          },
        )}
        onClick={(e) => {
          e.stopPropagation();
          deleteOrder();
        }}
      >
        {deletingOrder ? (
          <LoadingIndicator sm={18} md={18} lg={18} />
        ) : (
          <XIcon className={classNames('w-4 h-4', { 'text-white': isRejected })} />
        )}
      </button>
    );
  }

  const renderAction = () => {
    return isRejected ? renderCloseIcon() : <PickUpDeliveryTime className="lg:flex hidden" />;
  };

  return (
    <div
      className={classNames('bg-card cursor-pointer transition-all py-3', className, {
        '!bg-danger': isRejected,
        'hover:bg-gray-1200': !isRejected,
        'layout-container': isDesktop,
        'px-md': !isDesktop,
      })}
      onClick={goToOrder}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex flex-col gap-0.5 items-start">{renderLabel()}</div>
        </div>

        <div className="flex items-center gap-3">
          {renderAction()}
          <ChevronRight className={classNames('h-5 w-5', { 'text-white': isRejected })} />
        </div>
      </div>
    </div>
  );
}
